// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import type { SelectChangeEvent } from '@mui/material/Select';
import { TRAYS, WEIGHT_UNITS, getCurrencySymbol, getWeightUnitOptions } from '@seedplanner/shared-utils';
import type { Dayjs } from 'dayjs';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import {
  Box,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
} from '../../lib/base';
import { ClearIcon } from '../../lib/icons';
import { formatDuration, validateNumInput } from '../../utils';
import { sortContainerSizes } from '../size/sortContainerSizes';
import { createSizeKey, getSizeParamsFromKey } from './sizeUtils';

export interface CropOrBlendToUseFormInput {
  readonly cropOrBlendSelected: CropData | BlendData | undefined;
  readonly itemCount: string; // A number represented as a string
  readonly harvestTime: Dayjs;
  readonly itemCountValidation: string;
  readonly pricePerItem: string; // A number represented a string
  readonly pricePerItemValidation: string;
  // countUnit is: "grams", "ounces", "trays", or a "size key" which is "size:unit:price:productId"
  readonly countUnit: string;
}
interface Size {
  readonly size: number;
  readonly unit: string; // "ounces" | "grams" | "trays" (only for crops)
  readonly price_per_item?: number;
  readonly product_id?: number;
}
export interface CropData {
  readonly type: 'crop';
  readonly id: string;
  readonly crop_name: string;
  readonly total_grow_time: number;
  readonly harvest_buffer_time: number;
  readonly yield_per_tray: string;
  readonly yield_unit: string;
  readonly buffer_percentage: number;
  readonly sizes: readonly Size[];
}
export interface BlendData {
  readonly type: 'blend';
  readonly id: string;
  readonly blend_name: string;
  readonly sizes: readonly Size[];
  readonly crops: {
    readonly edges: {
      readonly node: {
        readonly id: string;
        readonly percent_of_blend: number;
        readonly crop?: {
          readonly id: string;
          readonly crop_name: string;
          readonly total_grow_time: number;
          readonly harvest_buffer_time: number;
          readonly yield_per_tray: string;
          readonly yield_unit: string;
          readonly buffer_percentage: number;
        } | null;
      };
    }[];
  };
}

export interface Props {
  readonly disableFields: boolean;
  readonly index: number;
  readonly formKey: number;
  readonly values: CropOrBlendToUseFormInput;
  readonly onChangeField: (
    index: number,
    fieldName: keyof CropOrBlendToUseFormInput,
    value: string | number | null | undefined | CropData | BlendData,
    validationFieldName?: string,
  ) => void;
  readonly onRemove: (index: number) => void;
  // readonly onEnter: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  readonly cropsData: CropData[];
  readonly blendsData: BlendData[];
}

export default function NewCropOrBlendToUseForm({
  index,
  disableFields,
  onChangeField,
  values,
  onRemove,
  cropsData,
  blendsData,
}: Props) {
  const { preferredWeightUnit, currencyCode } = useUserContext();
  const currencySymbol = getCurrencySymbol(currencyCode);
  const onCropOrBlendIdChange = (e: SelectChangeEvent<string | null>) => {
    const idSelected = e.target.value;
    // biome-ignore lint/suspicious/noDoubleEquals: <explanation>
    if (idSelected == values.cropOrBlendSelected?.id) {
      // Do nothing if crop doesn't change
      return;
    }
    const cropOrBlendSelected: CropData | BlendData | undefined = [...cropsData, ...blendsData].find(
      // biome-ignore lint/suspicious/noDoubleEquals: <explanation>
      (cropOrBlend) => cropOrBlend.id == idSelected,
    );
    onChangeField(index, 'cropOrBlendSelected', cropOrBlendSelected);
  };
  const onItemCountChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => onChangeField(index, 'itemCountValidation', msg),
      validateGT: 0,
      // Trays and containers have to be integers
      validateInt: values.countUnit !== WEIGHT_UNITS.OUNCES && values.countUnit !== WEIGHT_UNITS.GRAMS,
    });
    onChangeField(index, 'itemCount', e.target.value);
  };
  const onItemCountBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => onChangeField(index, 'itemCountValidation', msg),
      validateGT: 0,
      isRequired: true,
      // Trays and containers have to be integers
      validateInt: values.countUnit !== WEIGHT_UNITS.OUNCES && values.countUnit !== WEIGHT_UNITS.GRAMS,
    });
  };
  const onCountUnitChange = (e: SelectChangeEvent<string>) => {
    onChangeField(index, 'countUnit', e.target.value);
    const { pricePerItem } = getSizeParamsFromKey(e.target.value);

    if (pricePerItem != null) {
      onChangeField(index, 'pricePerItem', pricePerItem.toString());
    }

    if (e.target.value === TRAYS) {
      validateNumInput({
        event: { target: { value: values.itemCount.toString() } } as React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement
        >,
        setValidationMessage: (msg) => onChangeField(index, 'itemCountValidation', msg),
        validateGT: 0,
        isRequired: true,
        validateInt: true,
      });
    } else {
      onChangeField(index, 'itemCountValidation', '');
    }
  };
  const onPricePerItemChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => onChangeField(index, 'pricePerItemValidation', msg),
      validateGT: 0,
      decimals: 2,
    });
    onChangeField(index, 'pricePerItem', e.target.value);
  };
  const onPricePerItemBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => onChangeField(index, 'pricePerItemValidation', msg),
      validateGT: 0,
      decimals: 2,
    });
  };

  const containerSizesSorted = sortContainerSizes(values.cropOrBlendSelected?.sizes ?? []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      sx={{ border: 1, borderColor: 'grey.400', borderRadius: '4px', p: 2 }}
    >
      <Grid container flexDirection="row" flexWrap="nowrap" alignItems="center" gap={1}>
        <Grid flexGrow="1">
          <FormControl fullWidth>
            <InputLabel id="crop-or-blend-selector">Crop/Blend</InputLabel>
            <Select
              aria-describedby="crop-or-blend-selector"
              id="crop-or-blend-selector"
              labelId="crop-or-blend-selector"
              label="Crop/Blend"
              value={values.cropOrBlendSelected ? values.cropOrBlendSelected.id : ''}
              onChange={onCropOrBlendIdChange}
              // onEnter={onEnter}
              required={true}
              autoFocus={true}
              disabled={disableFields}
            >
              {blendsData.length > 0 && cropsData.length > 0 && <ListSubheader>Crops</ListSubheader>}
              {cropsData.map((crop) => {
                const cropLabel = `${crop.crop_name} (${formatDuration(crop.total_grow_time)})`;
                return (
                  <MenuItem key={crop.id} value={crop.id}>
                    {cropLabel}
                  </MenuItem>
                );
              })}
              {blendsData.length > 0 && cropsData.length > 0 && <ListSubheader>Blends</ListSubheader>}
              {blendsData.map((blend) => {
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                const blendLabel = `${blend.blend_name} (${formatDuration(
                  Math.max(...blend.crops.edges.map(({ node }) => node.crop?.total_grow_time ?? -1)),
                )})`;
                return (
                  <MenuItem key={blend.id} value={blend.id}>
                    {blendLabel}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid>
          <IconButton aria-label="remove-crop-or-blend" onClick={() => onRemove(index)}>
            <ClearIcon />
          </IconButton>
        </Grid>
      </Grid>
      <Grid display="flex" flexDirection="row" flexWrap="nowrap" gap={2}>
        <Grid flexGrow="1">
          <TextField
            aria-describedby="count"
            id="count"
            label="Count"
            value={values.itemCount}
            onChange={onItemCountChange}
            onBlur={onItemCountBlur}
            hasSubtext
            subtext={values.itemCountValidation}
            error={values.itemCountValidation !== ''}
            // onEnter={onEnter}
            required
            numberMin={0}
            type="number"
            inputMode="decimal"
            disabled={disableFields}
          />
        </Grid>
        <Grid sx={{ minWidth: 'unset' }}>
          <FormControl fullWidth>
            <InputLabel id="count-unit-selector">Units</InputLabel>
            <Select
              aria-describedby="count-unit-selector"
              id="count-unit-selector"
              labelId="count-unit-selector"
              label="Units"
              value={values.countUnit}
              onChange={onCountUnitChange}
              // onEnter={onEnter}
              required={true}
              disabled={disableFields}
            >
              {getWeightUnitOptions(preferredWeightUnit)
                // Only allow trays for crops
                .concat(values.cropOrBlendSelected?.type === 'crop' ? TRAYS : [])
                .map((sizeUnit) => {
                  // sizeUnit = 'ounces' | 'grams' | 'trays'
                  const sizeKey = createSizeKey({
                    size: null,
                    unit: sizeUnit,
                    pricePerItem: null,
                    productId: null,
                  });
                  return (
                    <MenuItem key={sizeKey} value={sizeKey}>
                      {sizeUnit}
                    </MenuItem>
                  );
                })
                .concat(
                  containerSizesSorted.map((sizeUnit) => {
                    const sizeKey = createSizeKey({
                      size: sizeUnit.size,
                      unit: sizeUnit.unit,
                      pricePerItem: sizeUnit.price_per_item ?? null,
                      productId: sizeUnit.product_id ?? null,
                    });
                    const pricePerItem =
                      sizeUnit.price_per_item == null ? '' : ` (${currencySymbol}${sizeUnit.price_per_item})`;
                    return (
                      <MenuItem key={sizeKey} value={sizeKey}>
                        {`${sizeUnit.size} ${sizeUnit.unit}${pricePerItem}`}
                      </MenuItem>
                    );
                  }),
                )}
            </Select>
          </FormControl>
        </Grid>
        <Grid>
          <TextField
            aria-describedby="price-per-item"
            id="price-per-item"
            label="Price Per Item (optional)"
            value={values.pricePerItem}
            onChange={onPricePerItemChange}
            onBlur={onPricePerItemBlur}
            hasSubtext
            subtext={values.pricePerItemValidation}
            error={values.pricePerItemValidation !== ''}
            // onEnter={onEnter}
            numberMin={0}
            numberStep={0.01}
            type="number"
            inputMode="decimal"
            disabled={disableFields}
            startAdornment={<span style={{ marginRight: 4 }}>{currencySymbol}</span>}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
